<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-6">
			<div class="flex justify-between items-center gap-4">
				<div class="flex items-center gap-4">
					<button
						type="button"
						class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
						@click="goBack"
					>
						<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
						</svg>
					</button>
					<div>
						<h1 class="text-2xl font-semibold text-gray-900 m-0">
							{{ crossSell?.name || 'Loading...' }}
						</h1>
						<p class="text-sm text-gray-500 mt-1 mb-0">Cross Sell ID: {{ routeId }}</p>
					</div>
				</div>
				<button
					type="button"
					:disabled="isSaving"
					class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50"
					@click="saveChanges"
				>
					<svg v-if="!isSaving" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
					</svg>
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</header>

		<!-- Loading State -->
		<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
			<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span class="ml-3 text-gray-500 text-sm">Loading cross sell...</span>
		</div>

		<!-- Main Content -->
		<main v-else class="p-6">
			<div class="max-w-3xl">
				<!-- Basic Properties Card -->
				<div class="bg-white rounded-xl shadow-sm p-6 mb-6">
					<h3 class="text-base font-semibold text-gray-900 m-0 mb-6 pb-3 border-b border-gray-200">Basic Properties</h3>
					
					<!-- Name -->
					<div class="mb-5">
						<label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
						<input
							v-model="crossSell.name"
							type="text"
							class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
						>
					</div>

					<!-- Preview Theme -->
					<div class="mb-5">
						<label class="block text-sm font-medium text-gray-700 mb-2">Preview Theme</label>
						<input
							v-model="crossSell.previewTheme"
							type="text"
							placeholder="Enter preview theme"
							class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
						>
					</div>

					<!-- Published -->
					<div class="flex items-center gap-3">
						<label class="text-sm font-medium text-gray-700">Published</label>
						<button
							type="button"
							class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors"
							:class="crossSell.published === 1 ? 'bg-orange-7' : 'bg-gray-300'"
							@click="crossSell.published = crossSell.published === 1 ? 0 : 1"
						>
							<span
								class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm"
								:class="crossSell.published === 1 ? 'left-[1.375rem]' : 'left-0.5'"
							></span>
						</button>
					</div>
				</div>

				<!-- Offering References Card -->
				<div class="bg-white rounded-xl shadow-sm p-6 mb-6">
					<h3 class="text-base font-semibold text-gray-900 m-0 mb-6 pb-3 border-b border-gray-200">Offering References</h3>
					
					<!-- Cross Sell ID -->
					<div class="mb-5">
						<label class="block text-sm font-medium text-gray-700 mb-2">Cross Sell ID</label>
						<div class="flex gap-3 items-center">
							<select
								v-model="crossSell.crosssellid"
								:disabled="crossSellIdNull"
								class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none disabled:opacity-50"
							>
								<option :value="null">Select an offering</option>
								<option v-for="offering in offerings" :key="offering.id" :value="offering.id">
									{{ offering.id }} | {{ offering.size }} - {{ offering.description }}
								</option>
							</select>
							<label class="flex items-center gap-2 text-sm text-gray-500 cursor-pointer whitespace-nowrap">
								<input
									type="checkbox"
									v-model="crossSellIdNull"
									@change="onCrossSellIdNullChange"
									class="w-4 h-4"
								>
								Null
							</label>
						</div>
					</div>

					<!-- Offering ID -->
					<div class="mb-5">
						<label class="block text-sm font-medium text-gray-700 mb-2">Offering ID</label>
						<div class="flex gap-3 items-center">
							<select
								v-model="crossSell.offeringid"
								:disabled="offeringIdNull"
								class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none disabled:opacity-50"
							>
								<option :value="null">Select an offering</option>
								<option v-for="offering in offerings" :key="offering.id" :value="offering.id">
									{{ offering.id }} | {{ offering.size }} - {{ offering.description }}
								</option>
							</select>
							<label class="flex items-center gap-2 text-sm text-gray-500 cursor-pointer whitespace-nowrap">
								<input
									type="checkbox"
									v-model="offeringIdNull"
									@change="onOfferingIdNullChange"
									class="w-4 h-4"
								>
								Null
							</label>
						</div>
					</div>

					<!-- Theme ID -->
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-2">Theme ID</label>
						<div class="flex gap-3 items-center">
							<select
								v-model="crossSell.themeid"
								:disabled="themeIdNull"
								class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none disabled:opacity-50"
							>
								<option :value="null">Select a theme</option>
								<option v-for="theme in themes" :key="theme.id" :value="theme.id">
									{{ theme.id }} | {{ theme.name }} - {{ theme.description }}
								</option>
							</select>
							<label class="flex items-center gap-2 text-sm text-gray-500 cursor-pointer whitespace-nowrap">
								<input
									type="checkbox"
									v-model="themeIdNull"
									@change="onThemeIdNullChange"
									class="w-4 h-4"
								>
								Null
							</label>
						</div>
					</div>
				</div>

				<!-- Numeric Properties Card -->
				<div class="bg-white rounded-xl shadow-sm p-6">
					<h3 class="text-base font-semibold text-gray-900 m-0 mb-6 pb-3 border-b border-gray-200">Numeric Properties</h3>
					
					<!-- Show Offering Option -->
					<div class="mb-5">
						<label class="block text-sm font-medium text-gray-700 mb-2">Show Offering Option</label>
						<div class="flex gap-3 items-center">
							<input
								v-model.number="crossSell.showOfferingOption"
								type="number"
								:disabled="showOfferingOptionNull"
								class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none disabled:opacity-50"
							>
							<label class="flex items-center gap-2 text-sm text-gray-500 cursor-pointer whitespace-nowrap">
								<input
									type="checkbox"
									v-model="showOfferingOptionNull"
									@change="onShowOfferingOptionNullChange"
									class="w-4 h-4"
								>
								Null
							</label>
						</div>
					</div>

					<!-- Serial Number -->
					<div class="mb-5">
						<label class="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
						<input
							v-model.number="crossSell.serialnumber"
							type="number"
							class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
						>
					</div>

					<!-- Photo Quantity -->
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-2">Photo Quantity</label>
						<input
							v-model.number="crossSell.photoQuantity"
							type="number"
							class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
						>
					</div>
				</div>
			</div>
		</main>
	</div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from 'sweetalert2';
import CrossSellService from 'services/CrossSellService';
import OfferingService from 'services/OfferingService';
import ThemeService from 'services/ThemeService';

export default defineComponent({
	name: 'EditCrossSellPage',
	setup() {
		const router = useRouter();
		const route = useRoute();

		const routeId = computed(() => route.params.id);
		const crossSell = ref({});
		const offerings = ref([]);
		const themes = ref([]);
		const isLoading = ref(true);
		const isSaving = ref(false);

		const crossSellIdNull = ref(false);
		const offeringIdNull = ref(false);
		const themeIdNull = ref(false);
		const showOfferingOptionNull = ref(false);

		const goBack = () => {
			router.push('/crosssell');
		};

		const loadData = async () => {
			isLoading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				const [crossSellData, offeringData, themeData] = await Promise.all([
					CrossSellService.get(routeId.value),
					OfferingService.getAll(params),
					ThemeService.getAll(params),
				]);
				crossSell.value = crossSellData;
				offerings.value = offeringData;
				themes.value = themeData;

				crossSellIdNull.value = crossSell.value.crosssellid === null;
				offeringIdNull.value = crossSell.value.offeringid === null;
				themeIdNull.value = crossSell.value.themeid === null;
				showOfferingOptionNull.value = crossSell.value.showOfferingOption === null;
			} catch (error) {
				console.error('Failed to load data:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load cross sell',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const saveChanges = async () => {
			isSaving.value = true;
			try {
				await CrossSellService.update(routeId.value, { ...crossSell.value });
				Swal.fire({
					title: 'Saved!',
					text: 'Cross sell has been updated.',
					icon: 'success',
					timer: 1500,
					showConfirmButton: false,
				});
			} catch (error) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save cross sell',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		const onCrossSellIdNullChange = () => {
			if (crossSellIdNull.value) {
				crossSell.value.crosssellid = null;
			}
		};

		const onOfferingIdNullChange = () => {
			if (offeringIdNull.value) {
				crossSell.value.offeringid = null;
			}
		};

		const onThemeIdNullChange = () => {
			if (themeIdNull.value) {
				crossSell.value.themeid = null;
			}
		};

		const onShowOfferingOptionNullChange = () => {
			if (showOfferingOptionNull.value) {
				crossSell.value.showOfferingOption = null;
			}
		};

		onMounted(() => {
			loadData();
		});

		return {
			routeId,
			crossSell,
			offerings,
			themes,
			isLoading,
			isSaving,
			crossSellIdNull,
			offeringIdNull,
			themeIdNull,
			showOfferingOptionNull,
			goBack,
			saveChanges,
			onCrossSellIdNullChange,
			onOfferingIdNullChange,
			onThemeIdNullChange,
			onShowOfferingOptionNullChange,
		};
	},
});
</script>

<style>
@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}
</style>
